(() => {
  'use strict';

  function pad(value) {
    return String(value).padStart(2, '0');
  }

  function getSource(section) {
    const registry = window.SMIBPlaylistRegistry;
    const sourceIndex = Number(section.dataset.playlistSourceIndex) || 1;
    const fallback = section.dataset.playlistSource || '';
    return registry?.resolve(sourceIndex, fallback) || { provider: 'unknown', id: '', embedUrl: '', url: fallback };
  }

  function showStatus(section, message) {
    const status = section.querySelector('[data-smib-playlist-status]');
    const loading = section.querySelector('[data-smib-playlist-loading]');
    if (loading) loading.hidden = true;
    if (status) {
      status.hidden = false;
      status.textContent = message;
    }
  }

  function initSpotify(section, source) {
    const embed = section.querySelector('[data-smib-playlist-embed]');
    const youtubeArea = section.querySelector('[data-smib-youtube-area]');
    const loading = section.querySelector('[data-smib-playlist-loading]');
    const status = section.querySelector('[data-smib-playlist-status]');
    const youtubeOnly = section.querySelector('[data-smib-youtube-only]');
    if (!embed || !source.embedUrl) return showStatus(section, 'Spotify playlist kon niet worden geladen.');

    if (youtubeArea) youtubeArea.hidden = true;
    if (youtubeOnly) youtubeOnly.hidden = true;
    if (loading) loading.hidden = true;
    if (status) status.hidden = true;
    embed.hidden = false;
    embed.classList.add('smib-c-playlist__embed--spotify');

    const iframe = document.createElement('iframe');
    iframe.src = source.embedUrl;
    iframe.title = section.dataset.playlistTitle || 'Spotify playlist';
    iframe.loading = 'eager';
    iframe.allow = 'autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture';
    iframe.referrerPolicy = 'strict-origin-when-cross-origin';
    embed.replaceChildren(iframe);
  }

  function fetchTitle(videoId, titleElement) {
    fetch(`https://www.youtube.com/oembed?url=${encodeURIComponent(`https://www.youtube.com/watch?v=${videoId}`)}&format=json`)
      .then(response => response.ok ? response.json() : Promise.reject())
      .then(data => {
        if (data?.title) titleElement.textContent = data.title;
      })
      .catch(() => {});
  }

  function createSlide(section, videoId, index, onActivate) {
    const layout = section.dataset.playlistLayout || 'slider';
    const slide = document.createElement('div');
    slide.className = 'smib-c-playlist__slide';

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'smib-c-playlist__thumb';
    button.dataset.playlistIndex = String(index);
    button.setAttribute('aria-label', `Speel video ${index + 1}`);

    const media = document.createElement('span');
    media.className = 'smib-c-playlist__thumb-media';
    const image = document.createElement('img');
    image.src = `https://i.ytimg.com/vi/${encodeURIComponent(videoId)}/hqdefault.jpg`;
    image.alt = '';
    image.loading = index < 4 ? 'eager' : 'lazy';
    image.width = 480;
    image.height = 360;
    const play = document.createElement('span');
    play.className = 'smib-c-playlist__play';
    play.setAttribute('aria-hidden', 'true');
    media.append(image, play);
    button.appendChild(media);

    if (layout === 'archive') {
      const content = document.createElement('span');
      content.className = 'smib-c-playlist__card-content';
      const meta = document.createElement('span');
      meta.className = 'smib-c-playlist__card-meta';
      const label = document.createElement('span');
      label.textContent = 'SMIB TUBE';
      const archive = document.createElement('span');
      archive.textContent = 'ARCHIVE';
      const title = document.createElement('span');
      title.className = 'smib-c-playlist__card-title';
      title.textContent = 'Video laden';
      meta.append(label, archive);
      content.append(meta, title);
      button.appendChild(content);
      fetchTitle(videoId, title);
    }

    button.addEventListener('click', () => onActivate(index, slide, button, videoId));
    slide.appendChild(button);
    return slide;
  }

  function initYouTube(section, source) {
    const registry = window.SMIBPlaylistRegistry;
    const playerElement = section.querySelector('[data-smib-playlist-player]');
    const playerWrap = section.querySelector('[data-smib-playlist-player-wrap]');
    const rail = section.querySelector('[data-smib-playlist-rail]');
    const loading = section.querySelector('[data-smib-playlist-loading]');
    const status = section.querySelector('[data-smib-playlist-status]');
    const previous = section.querySelector('[data-smib-playlist-previous]');
    const next = section.querySelector('[data-smib-playlist-next]');
    const counter = section.querySelector('[data-smib-playlist-counter]');
    const currentTitle = section.querySelector('[data-smib-playlist-current-title]');
    const youtubeArea = section.querySelector('[data-smib-youtube-area]');
    const embed = section.querySelector('[data-smib-playlist-embed]');
    const layout = section.dataset.playlistLayout || 'slider';
    const loop = section.dataset.loop === 'true';

    if (!registry || !playerElement || !rail || !source.id) return showStatus(section, 'Voeg een geldige YouTube playlist toe.');
    if (youtubeArea) youtubeArea.hidden = false;
    const youtubeOnly = section.querySelector('[data-smib-youtube-only]');
    if (youtubeOnly) youtubeOnly.hidden = false;
    if (embed) embed.hidden = true;
    if (status) status.hidden = true;

    registry.loadYouTubeApi().then(() => {
      let player;
      let playlist = [];
      let attempts = 0;
      let expandedSlide = null;

      function updateActive() {
        if (!player || !playlist.length) return;
        const currentIndex = Math.max(0, player.getPlaylistIndex?.() ?? 0);
        rail.querySelectorAll('[data-playlist-index]').forEach(button => {
          const active = Number(button.dataset.playlistIndex) === currentIndex;
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-current', String(active));
        });
        if (counter) counter.textContent = `${pad(currentIndex + 1)} / ${pad(playlist.length)}`;
        const data = player.getVideoData?.();
        if (currentTitle && data?.title) currentTitle.textContent = data.title;
      }

      function closeInlinePlayer() {
        if (!expandedSlide) return;
        expandedSlide.querySelector('[data-smib-inline-player]')?.remove();
        expandedSlide.querySelector('.smib-c-playlist__thumb')?.removeAttribute('hidden');
        expandedSlide.classList.remove('is-expanded');
        expandedSlide = null;
      }

      function openInlinePlayer(slide, button, videoId) {
        closeInlinePlayer();
        const wrapper = document.createElement('div');
        wrapper.className = 'smib-c-playlist__inline-player';
        wrapper.dataset.smibInlinePlayer = '';
        const iframe = document.createElement('iframe');
        iframe.src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(videoId)}?autoplay=1&rel=0`;
        iframe.title = 'SMIB video';
        iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
        iframe.allowFullscreen = true;
        const close = document.createElement('button');
        close.type = 'button';
        close.className = 'smib-c-playlist__inline-close';
        close.setAttribute('aria-label', 'Sluit video');
        close.textContent = '×';
        close.addEventListener('click', closeInlinePlayer);
        wrapper.append(iframe, close);
        button.hidden = true;
        slide.prepend(wrapper);
        slide.classList.add('is-expanded');
        expandedSlide = slide;
        requestAnimationFrame(() => slide.scrollIntoView({ behavior: 'smooth', block: 'center' }));
      }

      function activate(index, slide, button, videoId) {
        if (layout === 'archive') {
          openInlinePlayer(slide, button, videoId);
          return;
        }
        player.playVideoAt?.(index);
        window.setTimeout(() => player.playVideo?.(), 120);
        if (playerWrap) requestAnimationFrame(() => playerWrap.scrollIntoView({ behavior: 'smooth', block: 'center' }));
      }

      function renderSlides() {
        playlist = player.getPlaylist?.() || [];
        if (!playlist.length) {
          attempts += 1;
          if (attempts < 40) return window.setTimeout(renderSlides, 250);
          return showStatus(section, 'Playlist kon niet worden geladen.');
        }

        if (loading) loading.hidden = true;
        rail.replaceChildren(...playlist.map((videoId, index) => createSlide(section, videoId, index, activate)));
        if (previous) previous.disabled = false;
        if (next) next.disabled = false;
        updateActive();
      }

      player = new window.YT.Player(playerElement, {
        width: '100%',
        height: '100%',
        playerVars: {
          listType: 'playlist',
          list: source.id,
          rel: 0,
          playsinline: 1,
          origin: window.location.origin,
        },
        events: {
          onReady(event) {
            event.target.cuePlaylist({ listType: 'playlist', list: source.id, index: 0 });
            event.target.setLoop(loop);
            renderSlides();
          },
          onStateChange: updateActive,
          onError: () => showStatus(section, 'Deze playlist kan niet worden afgespeeld.'),
        },
      });

      previous?.addEventListener('click', () => player.previousVideo?.());
      next?.addEventListener('click', () => player.nextVideo?.());
    }).catch(error => {
      console.error('[SMIB Playlist]', error);
      showStatus(section, 'YouTube kon niet worden geladen.');
    });
  }

  function init(section) {
    if (section.dataset.smibPlaylistReady === 'true') return;
    section.dataset.smibPlaylistReady = 'true';
    const source = getSource(section);

    if (source.provider === 'spotify') initSpotify(section, source);
    else if (source.provider === 'youtube') initYouTube(section, source);
    else showStatus(section, 'Voeg een YouTube of Spotify playlist toe in de centrale theme settings.');
  }

  function initAll(root = document) {
    const sections = [];
    if (root instanceof Element && root.matches('[data-smib-component="playlist"]')) sections.push(root);
    sections.push(...root.querySelectorAll?.('[data-smib-component="playlist"]') || []);
    sections.forEach(init);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll(document), { once: true });
  else initAll(document);

  document.addEventListener('shopify:section:load', event => initAll(event.target));
})();
